import React, { useState, useCallback, useEffect } from "react";
import {
    Container,
    Grid,
    Button,
    FormControl,
    Typography,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormGroup,
    FormControlLabel,
    Switch,
    List,
    ListItem,
    IconButton,
    ListItemButton,
    ListItemText,
    Autocomplete
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InputIcon from '@mui/icons-material/Input';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RichText from '../../../../../common/EditorMCE'
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useNavigate, useParams } from 'react-router-dom';
import { STATES } from "../../../../../constants/Geography"
import { GetOrg, CreateOrg, UpdateOrg, GetBillingGroups } from "../../../../../api";
import dayjs from 'dayjs';

const formatPhoneNumber = value => {
    const cleaned = value.replace(/\D/g, "")
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

    if (!match) return value;

    return [
        match[1] && `(${match[1]}`,
        match[2] && `) ${match[2]}`,
        match[3] && `-${match[3]}`
    ].filter(Boolean).join("")
}

const formatZip = value => {
    const cleaned = value.replace(/\D/g, "")
    const match = cleaned.match(/^(\d{0,5})(\d{0,4})$/)

    if (!match) return value;

    return [
        match[1] && `${match[1]}`,
        match[2] && `-${match[2]}`
    ].filter(Boolean).join("")
}

const emailTest = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/

let disclaimerRef;

const isEditorAlive = editor => editor && !editor.destroyed && editor.getBody()
const isEditorEmpty = editor => {
  if (!isEditorAlive(editor)) return true
  return editor.getContent({ format: 'text' }).trim() === '' && !editor.getBody().querySelector('img');
}

const EditOrg = ({ user: u, access }) => {

    const params = useParams();

    const [org, setOrg] = useState({})
    const [selectedFields, setSelectedFields] = useState({})
    const [newFields, setNewFields] = useState({})
    const [editingField, setEditingField] = useState({}) // { [field]: { i, value } }
    const [emailOptions, setEmailOptions] = useState([])
    const [billingGroups, setBillingGroups] = useState({}) // { [parentOrgId]: [...groups] }

    const navigate = useNavigate();

    const goBack = useCallback(num => {
        if (window.history.state && window.history.state.idx > 0) navigate(num)
        else navigate('/', { replace: true })
    }, [navigate])

    const handleSubmit = useCallback(e => {
        e.preventDefault()
        
        // Validate emails
        if (!org.email?.length) {
            return
        }
        
        // Save email options to localStorage
        try {
            if (localStorage && org.email?.length) {
                const emailOptionsCopy = [...emailOptions]
                org.email.forEach(email => emailOptionsCopy.find(e => e.toLowerCase() === email.toLowerCase()) || emailOptionsCopy.push(email))
                localStorage.setItem('clinicConsultEmails', JSON.stringify(emailOptionsCopy))
            }
        } catch (e) { }
        
        const orgData = { ...org, parentOrg: access.org.id, billingGroup: (org.billingGroup || []).map(g => g.id || g) }
        if (access.org.id === params.org && disclaimerRef?.current) {
            const settings = { ...orgData.settings }
            if (!isEditorEmpty(disclaimerRef.current))
                settings.disclaimer = disclaimerRef.current.getContent()
            else
                delete settings.disclaimer
            orgData.settings = settings
        }

        if (org.id)
            UpdateOrg(org.id, orgData).then(() => goBack(-1)).catch(e => e)
        else
            CreateOrg(orgData).then(() => goBack(-1)).catch(e => e)

    }, [goBack, access.org.id, org, emailOptions, params.org])

    useEffect(() => {

        if (!access?.admin)
            navigate('/', { replace: true })
        else {
            params.org && GetOrg(params.org).then(r => {
                if (r.email && typeof r.email === 'string') r.email = [r.email]
                setOrg(r)
            })
        }

    }, [params.org, access, navigate])

    useEffect(() => {
        try {
            if (localStorage)
                setEmailOptions(JSON.parse(localStorage.getItem('clinicConsultEmails')) || [])
        } catch (e) { }
    }, [])

    const updateOrg = e => {
        let { name, value } = e.target
        if (name === 'phone') value = formatPhoneNumber(value)
        else if (name === 'aetitle') value = value.toLowerCase().replace(/[^a-z0-9]/g, '')
        else if (name === 'zip') value = formatZip(value)
        setOrg(x => ({ ...x, [name]: value }))
    }
    const updateOrgArray = (e, i) => {
        setOrg(x => {
            const aet = [...x[e.target.name]]
            aet[i] = e.target.value
            return { ...x, [e.target.name]: aet }
        })
    }
    const updateOrgSetting = e => setOrg(x => {
        const settings = { ...x.settings }
        settings[e.target.name] = e.target.checked
        return { ...x, settings }
    })

    const updateAutoDeleteSetting = (field, value) => setOrg(x => {
        const settings = { ...x.settings }
        const autoDelete = { ...settings.autoDelete }
        autoDelete[field] = value
        settings.autoDelete = autoDelete
        return { ...x, settings }
    })

    const clearAutoDelete = () => setOrg(x => {
        const settings = { ...x.settings }
        delete settings.autoDelete
        return { ...x, settings }
    })

    const generateAE = () => {
        if (!org.aetitle?.length) {
            const { name, email } = org
            const firstEmail = Array.isArray(email) ? email[0] : email
            const domain = firstEmail?.split("@")[1]?.split(".")[0] || "";
            const cleanName = name?.replace(/\s+/g, "").substring(0, 6) || "";
            setOrg(x => ({ ...x, 'aetitle': (domain || cleanName).substring(0, 16).toLowerCase() }))
        }
    }

    const reorderField = (field, dragIndex, dropIndex) => {
        if (dragIndex === dropIndex) return
        setOrg(x => {
            const settings = { ...x.settings }
            const fields = [...settings[field]]
            const [item] = fields.splice(dragIndex, 1)
            fields.splice(dropIndex, 0, item)
            settings[field] = fields
            return { ...x, settings }
        })
    }

    const removeField = (field, i) =>
        setOrg(x => {
            const settings = { ...x.settings }
            const fields = [...settings[field]]
            fields.splice(i, 1)
            settings[field] = fields
            return { ...x, settings }
        })
    const addField = (field) => {
        if (newFields[field])
            setOrg(x => {
                const settings = { ...x.settings }
                if (!(field in settings))
                    settings[field] = []
                const fields = [...settings[field], newFields[field]]
                settings[field] = fields
                return { ...x, settings }
            })
        setNewFields(v=>({...v, [field]: ''}))
    }

    const editField = (field, i) => setEditingField(v => ({ ...v, [field]: { i, value: org.settings[field][i] } }))

    const saveEditField = (field) => {
        const { i, value } = editingField[field]
        if (value?.trim())
            setOrg(x => {
                const settings = { ...x.settings }
                const fields = [...settings[field]]
                fields[i] = value
                settings[field] = fields
                return { ...x, settings }
            })
        setEditingField(v => ({ ...v, [field]: undefined }))
    }

    if (params.org && !org.id) return <React.Fragment />

    return (
        <Container>
            <form onSubmit={handleSubmit}>
                <Grid container sx={{ mb: 3 }} spacing={3} justifyContent="left" maxWidth="sm">
                    <Typography component="h1" variant="h4">{params.org ? 'Edit Org' : 'New Org'}</Typography>
                    <FormControl sx={{ mb: 1, mt: 1 }} fullWidth variant="standard">
                        <TextField required name="name" label="Name" value={org.name || ''} onChange={updateOrg} />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <Autocomplete
                            id="emails"
                            freeSolo={true}
                            multiple
                            name="emails"
                            filterSelectedOptions
                            options={emailOptions}
                            value={org.email || []}
                            clearOnBlur={true}
                            onBlur={e => {
                                const val = e.target?.value?.trim()
                                if (val && emailTest.test(val)) {
                                    setOrg(x => ({ ...x, email: [...(x.email || []), val] }))
                                }
                            }}
                            onChange={(e, v) => setOrg(x => ({ ...x, email: v.map(e => e.trim()).filter(e => emailTest.test(e)) }))}
                            renderInput={(params) => (
                                <TextField {...params} variant="standard" label="Emails *" error={!org.email?.length} helperText={!org.email?.length ? "At least one email is required" : "First email address will appear on reports"} />
                            )}
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField name="phone" label="Phone Number" value={org.phone || ''} onChange={updateOrg} inputProps={{ maxLength: 14 }} />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField name="address" label="Address" value={org.address || ''} onChange={updateOrg} />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField name="city" label="City" value={org.city || ''} onChange={updateOrg} />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="outlined">
                        <InputLabel id="state-label">State</InputLabel>
                        <Select labelId="state-label" name="state" label="State" value={org.state || ''} onChange={updateOrg}>
                            {STATES.map(state => (
                                <MenuItem key={state.value} value={state.value}>
                                    {state.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField name="zip" label="Zip" value={org.zip || ''} onChange={updateOrg} inputProps={{ maxLength: 10 }} />
                    </FormControl>
                    {(org.aetitle?.map && (
                        org.aetitle.map((aet, i) => (
                            <FormControl key={i} sx={{ mb: 1 }} fullWidth variant="standard">
                                <TextField name="aetitle" label="AETitle" value={aet} onChange={e => updateOrgArray(e, i)} inputProps={{ maxLength: 16 }} />
                            </FormControl>
                        ))
                    )) || (
                            <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                <TextField required name="aetitle" label="AETitle" value={org.aetitle || ''} onFocus={generateAE} onChange={updateOrg} inputProps={{ maxLength: 16 }} />
                            </FormControl>
                        )}
                        <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                            <FormGroup>
                                {access.org.id === params.org ? (<>
                                    <FormControlLabel control={<Switch checked={!!org.settings?.emailReportWriter} name="emailReportWriter" onChange={updateOrgSetting} />} label="Copy report writer on reports" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.viewIncompleteReports} name="viewIncompleteReports" onChange={updateOrgSetting} />} label="Allow incomplete reports to be viewed" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.duplicateBillingCodes} name="duplicateBillingCodes" onChange={updateOrgSetting} />} label="Allow duplicate billing codes" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.requireBilling} name="requireBilling" onChange={updateOrgSetting} />} label="Require billing code on consult request" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.disableRequestee} name="disableRequestee" onChange={updateOrgSetting} />} label="Disable requestee on consult request" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.requirePhysician} name="requirePhysician" onChange={updateOrgSetting} />} label="Require physician on consult request" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.prelim} name="prelim" onChange={updateOrgSetting} />} label="Preliminary writers" />
                                    <FormControlLabel control={<Switch checked={!!org.settings?.reportPrelim} name="reportPrelim" onChange={updateOrgSetting} />} label="Show preliminary writer on reports" />
                                    <Grid container sx={{ mt: 3, mb: 2 }} justifyContent="left" maxWidth="sm">
                                        <Typography component="h1" variant="h5">Auto-Delete Studies</Typography>
                                        {org.settings?.autoDelete ? (
                                            <>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <FormControlLabel 
                                                        control={<Switch checked={!!org.settings.autoDelete.enabled} onChange={e => updateAutoDeleteSetting('enabled', e.target.checked)} />} 
                                                        label="Enable auto-delete" 
                                                    />
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            label="Start Date"
                                                            value={org.settings.autoDelete.startDate ? dayjs(org.settings.autoDelete.startDate) : null}
                                                            onChange={newValue => updateAutoDeleteSetting('startDate', newValue ? newValue.format('YYYY-MM-DD') : null)}
                                                        />
                                                    </LocalizationProvider>
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <TextField
                                                        label="Days to Keep Studies"
                                                        type="number"
                                                        value={org.settings.autoDelete.days || ''}
                                                        onChange={e => updateAutoDeleteSetting('days', parseInt(e.target.value) || null)}
                                                        inputProps={{ min: 1 }}
                                                        helperText="Number of days before studies are deleted"
                                                    />
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <FormControlLabel 
                                                        control={<Switch checked={!!org.settings.autoDelete.applyToChildren} onChange={e => updateAutoDeleteSetting('applyToChildren', e.target.checked)} />} 
                                                        label="Apply to child organizations (clinics)" 
                                                    />
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <Button variant="outlined" color="error" onClick={clearAutoDelete} startIcon={<DeleteIcon />}>
                                                        Clear Auto-Delete Settings
                                                    </Button>
                                                </FormControl>
                                            </>
                                        ) : (
                                            <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                <Button variant="outlined" onClick={() => updateAutoDeleteSetting('enabled', false)} startIcon={<InputIcon />}>
                                                    Configure Auto-Delete
                                                </Button>
                                            </FormControl>
                                        )}
                                    </Grid>
                                </>) : (<>
                                    {org.orgs?.map(parentOrg => {
                                        const parentId = parentOrg.id || parentOrg
                                        const parentName = parentOrg.name || parentId
                                        const groups = billingGroups[parentId]
                                        if (!groups) {
                                            GetBillingGroups(parentId).then(g => setBillingGroups(prev => ({ ...prev, [parentId]: g }))).catch(() => setBillingGroups(prev => ({ ...prev, [parentId]: [] })))
                                            return null
                                        }
                                        if (!groups.length) return null
                                        const current = (org.billingGroup || []).find(g => (g.org?.id || g.org) === parentId)
                                        const currentVal = current?.id || current || ''
                                        return (
                                            <FormControl key={parentId} sx={{ mb: 2 }} fullWidth variant="outlined">
                                                <InputLabel id={`bg-label-${parentId}`}>Billing Group ({parentName})</InputLabel>
                                                <Select labelId={`bg-label-${parentId}`} label={`Billing Group (${parentName})`}
                                                    value={currentVal}
                                                    onChange={e => setOrg(x => {
                                                        const bg = (x.billingGroup || []).filter(g => (g.org?.id || g.org) !== parentId)
                                                        if (e.target.value) bg.push(groups.find(g => g.id === e.target.value))
                                                        return { ...x, billingGroup: bg }
                                                    })}>
                                                    <MenuItem value=""><em>None (Default Pricing)</em></MenuItem>
                                                    {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        )
                                    })}
                                    <FormControlLabel control={<Switch checked={!!org.settings?.hideStudiesFromParent} name="hideStudiesFromParent" onChange={updateOrgSetting} />} label="Hide studies from parent organization clinic view" />
                                    {access.org.settings?.scheduler && (
                                        <FormControlLabel control={<Switch checked={!!org.settings?.enableScheduling} name="enableScheduling" onChange={updateOrgSetting} />} label="Enable scheduling" />
                                    )}
                                    <Grid container sx={{ mt: 3, mb: 2 }} justifyContent="left" maxWidth="sm">
                                        <Typography component="h1" variant="h5">Auto-Delete Studies Override</Typography>
                                        {org.settings?.autoDelete ? (
                                            <>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <FormControlLabel 
                                                        control={<Switch checked={!!org.settings.autoDelete.enabled} onChange={e => updateAutoDeleteSetting('enabled', e.target.checked)} />} 
                                                        label="Enable auto-delete for this organization" 
                                                    />
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            label="Start Date"
                                                            value={org.settings.autoDelete.startDate ? dayjs(org.settings.autoDelete.startDate) : null}
                                                            onChange={newValue => updateAutoDeleteSetting('startDate', newValue ? newValue.format('YYYY-MM-DD') : null)}
                                                        />
                                                    </LocalizationProvider>
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <TextField
                                                        label="Days to keep studies"
                                                        type="number"
                                                        value={org.settings.autoDelete.days || ''}
                                                        onChange={e => updateAutoDeleteSetting('days', parseInt(e.target.value) || null)}
                                                        inputProps={{ min: 1 }}
                                                        helperText="Number of days before studies are deleted"
                                                    />
                                                </FormControl>
                                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                    <Button variant="outlined" color="error" onClick={clearAutoDelete} startIcon={<DeleteIcon />}>
                                                        Clear Override (Use Parent Settings)
                                                    </Button>
                                                </FormControl>
                                            </>
                                        ) : (
                                            <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                                <Button variant="outlined" onClick={() => updateAutoDeleteSetting('enabled', false)} startIcon={<InputIcon />}>
                                                    Override Parent Auto-Delete
                                                </Button>
                                            </FormControl>
                                        )}
                                    </Grid>
                                </>)}
                            </FormGroup>
                        </FormControl>
                    {access.org.id === params.org && (
                        <>
                            <Grid container sx={{ mt: 3 }} justifyContent="left" maxWidth="sm">
                                <Typography component="h1" variant="h4">Report Fields</Typography>
                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                    <List sx={{ maxHeight: 300, overflow: "auto" }}>
                                        {Array.isArray(org.settings?.reportFields) && org.settings?.reportFields.map((label, i) => (
                                            <ListItem key={`${i}|${label}`} disablePadding draggable
                                                onDragStart={e => { e.dataTransfer.setData('text/plain', i); const img = document.createElement('div'); img.textContent = label; img.style.cssText = 'position:absolute;top:-1000px;background:#121212;padding:8px;border:1px solid #ccc;border-radius:4px;'; document.body.appendChild(img); e.dataTransfer.setDragImage(img, 0, 0); setTimeout(() => document.body.removeChild(img), 0) }}
                                                onDragOver={e => e.preventDefault()}
                                                onDrop={e => reorderField('reportFields', parseInt(e.dataTransfer.getData('text/plain')), i)}
                                                sx={{ cursor: 'grab' }}
                                                secondaryAction={(selectedFields.reportFields === i) && <><IconButton edge="end" aria-label="edit" onClick={() => editField('reportFields', i)}><EditIcon /></IconButton><IconButton edge="end" aria-label="delete" onClick={() => removeField('reportFields', i)}><DeleteIcon /></IconButton></>}>
                                                {editingField.reportFields?.i === i ? (
                                                    <TextField autoFocus size="small" fullWidth value={editingField.reportFields.value} onChange={e => setEditingField(v => ({ ...v, reportFields: { ...v.reportFields, value: e.target.value } }))} onBlur={() => saveEditField('reportFields')} onKeyDown={e => e.key === 'Enter' && saveEditField('reportFields')} />
                                                ) : (
                                                    <ListItemButton onClick={() => setSelectedFields(e=>({...e, reportFields: i}))} selected={selectedFields.reportFields === i}>
                                                        <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                                        <ListItemText primary={label} />
                                                    </ListItemButton>
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>
                                </FormControl>
                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                    <TextField name="report" label="Label" value={newFields.reportFields || ''} onChange={e => setNewFields(v=>({...v, reportFields: e.target.value}))} />
                                    <Button onClick={()=>addField('reportFields')} startIcon={<InputIcon />}>Add</Button>
                                </FormControl>
                            </Grid>
                        </>
                    )}
                    {access.org.id === params.org && (
                        <Grid container sx={{ mt: 3 }} justifyContent="left" maxWidth="sm">
                            <FormControl fullWidth variant="standard">
                                <Typography component="h1" variant="h4">Disclaimer</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>This will appear at the bottom of reports</Typography>
                                <RichText
                                    onEditorRef={ref => disclaimerRef=ref}
                                    initialValue={org.settings?.disclaimer || '<p></p>'}
                                />
                            </FormControl>
                        </Grid>
                    )}
                    <><Grid container sx={{ mt: 3 }} justifyContent="left" maxWidth="sm">
                        <Typography component="h1" variant="h4">Consult Fields</Typography>
                        <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                            <List sx={{ maxHeight: 300, overflow: "auto" }}>
                                {Array.isArray(org.settings?.consultFields) && org.settings?.consultFields.map((label, i) => (
                                    <ListItem key={`${i}|${label}`} disablePadding draggable
                                        onDragStart={e => { e.dataTransfer.setData('text/plain', i); const img = document.createElement('div'); img.textContent = label; img.style.cssText = 'position:absolute;top:-1000px;background:#121212;padding:8px;border:1px solid #ccc;border-radius:4px;'; document.body.appendChild(img); e.dataTransfer.setDragImage(img, 0, 0); setTimeout(() => document.body.removeChild(img), 0) }}
                                        onDragOver={e => e.preventDefault()}
                                        onDrop={e => reorderField('consultFields', parseInt(e.dataTransfer.getData('text/plain')), i)}
                                        sx={{ cursor: 'grab' }}
                                        secondaryAction={(selectedFields.consultFields === i) && <><IconButton edge="end" aria-label="edit" onClick={() => editField('consultFields', i)}><EditIcon /></IconButton><IconButton edge="end" aria-label="delete" onClick={() => removeField('consultFields', i)}><DeleteIcon /></IconButton></>}>
                                        {editingField.consultFields?.i === i ? (
                                            <TextField autoFocus size="small" fullWidth value={editingField.consultFields.value} onChange={e => setEditingField(v => ({ ...v, consultFields: { ...v.consultFields, value: e.target.value } }))} onBlur={() => saveEditField('consultFields')} onKeyDown={e => e.key === 'Enter' && saveEditField('consultFields')} />
                                        ) : (
                                            <ListItemButton onClick={() => setSelectedFields(e=>({...e, consultFields: i}))} selected={selectedFields.consultFields === i}>
                                                <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                                <ListItemText primary={label} />
                                            </ListItemButton>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </FormControl>
                        <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                            <TextField name="consult" label="Label" value={newFields.consultFields || ''} onChange={e => setNewFields(v=>({...v, consultFields: e.target.value}))} />
                            <Button onClick={()=>addField('consultFields')} startIcon={<InputIcon />}>Add</Button>
                        </FormControl>
                    </Grid></>
                    {access.org.id === params.org && org.settings?.scheduler && (
                        <><Grid container sx={{ mt: 3 }} justifyContent="left" maxWidth="sm">
                            <Typography component="h1" variant="h4">Mobile Consult Fields</Typography>
                            <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                <List sx={{ maxHeight: 300, overflow: "auto" }}>
                                    {Array.isArray(org.settings?.mobileConsultFields) && org.settings?.mobileConsultFields.map((label, i) => (
                                        <ListItem key={`${i}|${label}`} disablePadding draggable
                                            onDragStart={e => { e.dataTransfer.setData('text/plain', i); const img = document.createElement('div'); img.textContent = label; img.style.cssText = 'position:absolute;top:-1000px;background:#121212;padding:8px;border:1px solid #ccc;border-radius:4px;'; document.body.appendChild(img); e.dataTransfer.setDragImage(img, 0, 0); setTimeout(() => document.body.removeChild(img), 0) }}
                                            onDragOver={e => e.preventDefault()}
                                            onDrop={e => reorderField('mobileConsultFields', parseInt(e.dataTransfer.getData('text/plain')), i)}
                                            sx={{ cursor: 'grab' }}
                                            secondaryAction={(selectedFields.mobileConsultFields === i) && <><IconButton edge="end" aria-label="edit" onClick={() => editField('mobileConsultFields', i)}><EditIcon /></IconButton><IconButton edge="end" aria-label="delete" onClick={() => removeField('mobileConsultFields', i)}><DeleteIcon /></IconButton></>}>
                                            {editingField.mobileConsultFields?.i === i ? (
                                                <TextField autoFocus size="small" fullWidth value={editingField.mobileConsultFields.value} onChange={e => setEditingField(v => ({ ...v, mobileConsultFields: { ...v.mobileConsultFields, value: e.target.value } }))} onBlur={() => saveEditField('mobileConsultFields')} onKeyDown={e => e.key === 'Enter' && saveEditField('mobileConsultFields')} />
                                            ) : (
                                                <ListItemButton onClick={() => setSelectedFields(e=>({...e, mobileConsultFields: i}))} selected={selectedFields.mobileConsultFields === i}>
                                                    <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                                    <ListItemText primary={label} />
                                                </ListItemButton>
                                            )}
                                        </ListItem>
                                    ))}
                                </List>
                            </FormControl>
                            <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                <TextField name="consult" label="Label" value={newFields.mobileConsultFields || ''} onChange={e => setNewFields(v=>({...v, mobileConsultFields: e.target.value}))} />
                                <Button onClick={()=>addField('mobileConsultFields')} startIcon={<InputIcon />}>Add</Button>
                            </FormControl>
                        </Grid></>
                    )}
                </Grid>
                <Grid container spacing={3} justifyContent="left">
                    <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>{params.org ? 'Save' : 'Create'}</Button>
                    <Button variant="outlined" color="primary" onClick={() => goBack(-1)} startIcon={<CloseIcon />}>Cancel</Button>
                </Grid>
            </form>
        </Container>
    )

}

export default EditOrg